import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Badge, Button } from "nlib/ui";
import { checkSelectedBusinessRpaMode } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import BusinessesActions from "actions/BusinessesActions";
import DataStatuses from "const/DataStatuses";
import DocumentPreview from "nlib/common/DocumentPreview";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import MultiButton from "nlib/common/MultiButton";
import React, { useCallback, useState } from "react";
import Transactions from "utils/Transactions";
import TransactionsActions from "actions/TransactionsActions";
import UiActions from "actions/UiActions";
import classNames from "classnames";
import useAttachDocument from "hooks/useAttachDocument";
import useShowCommonModal from "hooks/useShowCommonModal";

const MatchDocument = ({ className, attachmentId, disabled, transaction, refetchTableData }) => {
  const { uiTexts, errors, messages } = useSelector(getTextsData);

  const {
    id: transactionId,
    documentId,
    type,
    status,
    documentStatus,
    documentPreview,
    matchedDocuments = 0,
    documentAttachment = {},
    documentRecogniseStatus,
    documentLastExportErrors = []
  } = transaction;

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const attachDocument = useAttachDocument({ id: transactionId, documentId, type });

  const advancedType = Transactions.getTransactionAdvancedType(transaction);

  const [fetching, setFetching] = useState(false);

  const recogniseError = documentRecogniseStatus === DocumentRecogniseStatuses.ERROR;

  const documentNotApproved = documentStatus && ![DataStatuses.EXPORTED, DataStatuses.TO_REPORT].includes(documentStatus);

  const handlePairButtonClick = useCallback(async() => {
    if (attachmentId) {
      await showCommonModal({
        text: messages.matchDocumentWhenAttachmentWarning
      });

      return;
    }

    const result = await dispatch(UiActions.showPairDocumentsWindow({ transaction }));

    if (result) {
      setFetching(true);
      dispatch(TransactionsActions.lockTransaction(transaction.id));
      if (result.documentId) {
        await dispatch(BusinessesActions.pairMatches(transaction.id, result.documentId));
      }
      if (result.file) {
        await attachDocument(result.file);
      }
      dispatch(TransactionsActions.unlockTransaction(transaction.id));
      setFetching(false);
      refetchTableData();
    }
  }, [
    dispatch,
    transaction,
    attachDocument,
    refetchTableData,
    showCommonModal,
    messages,
    attachmentId
  ]);

  const handleDocumentEdit = useCallback(async() => {
    const result = await dispatch(UiActions.showEditDocumentWindow({ documentId }));

    if (result) refetchTableData();
  }, [dispatch, documentId, refetchTableData]);

  const handleUnmatchButtonClick = useCallback(async() => {
    const result = await dispatch(UiActions.showModal(messages.documentUnmatchConfirm, null, true));

    if (!result) return;
    await dispatch(BusinessesActions.unpairMatches(transactionId, documentId));
  }, [dispatch, messages, transactionId, documentId]);

  if (documentId) {
    return (
      <div className={classNames(Css.matchDocument, className)}>
        {(status === DataStatuses.TO_REVIEW) && !!documentLastExportErrors?.length && (
          <div
            className={Css.errorMessage}
            data-tooltip={`${errors.exportTransactionError}: ${documentLastExportErrors.join("; ")}`}
            data-tooltip-style="negative">
            <Icons.Warning weight="bold" />
          </div>
        )}
        <DocumentPreview
          className={Css.preview}
          error={recogniseError}
          transactionId={transactionId}
          hasAttachment={!!documentAttachment.key}
          documentPreview={documentPreview} />
        <MultiButton
          className={Css.multiButton}
          buttons={[
            !recogniseError && (!documentNotApproved
              || ![DataStatuses.EXPORTED, DataStatuses.TO_REPORT].includes(status)) && {
              key: "match",
              outline: true,
              success: !disabled && documentNotApproved,
              disabled,
              icon: documentNotApproved ? Icons.Check : Icons.File,
              className: Css.button,
              onClick: handleDocumentEdit,
              content: <span>{documentNotApproved ? uiTexts.approveDocument : uiTexts.openDocument}</span>
            },
            ![DataStatuses.EXPORTED, DataStatuses.TO_REPORT].includes(status) && {
              key: "unmatch",
              outline: true,
              danger: true,
              disabled,
              icon: Icons.X,
              className: Css.button,
              onClick: handleUnmatchButtonClick,
              content: <span>{uiTexts.unmatchDocument}</span>
            }
          ]} />
      </div>
    );
  }

  return ![DataStatuses.EXPORTED, DataStatuses.TO_REPORT].includes(status)
    && (!rpaMode || advancedType === AdvancedTransactionTypes.BANK_FEED) && (
    <div className={classNames(Css.matchDocument, className)}>
      <Button
        outline alternative
        disabled={disabled || fetching}
        icon={matchedDocuments ? null : Icons.FileSearch}
        className={Css.button}
        onClick={handlePairButtonClick}>
        <span>
          {fetching
            ? `${uiTexts.matching}...`
            : (
              matchedDocuments
                ? (
                  <>
                    <Badge
                      counter inverted
                      className={Css.badge}
                      theme="alternative"
                      value={matchedDocuments} />
                    <span>{uiTexts.matchingDocuments}</span>
                  </>
                )
                : uiTexts.matchDocument
            )}
        </span>
      </Button>
    </div>
  );
};

export default React.memo(MatchDocument);
